import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Search,
  Loader2,
  TrendingUp,
  Users,
  Target,
  Package,
  DollarSign,
  XCircle,
  ArrowLeft,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Download,
  ExternalLink,
  ShoppingCart,
  Image as ImageIcon,
  Truck,
  Tag,
  Globe,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';
import apiClient from '../services/apiClient';
import {
  checkExtensionInstalled,
  sendToExtension,
  downloadExtensionLogs,
  isExtensionVersionAtLeast,
  requestExtensionUpdate,
} from '../services/extensionBridge';
import CreditsBadge from '../components/CreditsBadge';
import TutorialVideoButton from '../components/TutorialVideoButton';

// Umbrales de negocio en moneda local de cada sitio.
// altaFacturacion: facturación mínima para considerar una pub "rentable"
// ticketAlto / ticketBajo: rangos de precio promedio para análisis de ticket
const SITES = {
  MCO: { name: 'Colombia',  currency: 'COP', flag: '🇨🇴', salesThreshold: 5_000_000, altaFacturacion: 1_200_000, ticketAlto: 64_000,  ticketBajo: 28_000  },
  MLA: { name: 'Argentina', currency: 'ARS', flag: '🇦🇷', salesThreshold:   500_000, altaFacturacion:   595_000, ticketAlto: 16_000,  ticketBajo:  7_000  },
  MLB: { name: 'Brasil',    currency: 'BRL', flag: '🇧🇷', salesThreshold:    10_000, altaFacturacion:     1_500, ticketAlto:     80,  ticketBajo:     35  },
  MLM: { name: 'México',    currency: 'MXN', flag: '🇲🇽', salesThreshold:    50_000, altaFacturacion:     7_000, ticketAlto:    272,  ticketBajo:    119  },
  MLC: { name: 'Chile',     currency: 'CLP', flag: '🇨🇱', salesThreshold: 2_000_000, altaFacturacion:   370_000, ticketAlto: 15_200,  ticketBajo:  6_650  },
  MLU: { name: 'Uruguay',   currency: 'UYU', flag: '🇺🇾', salesThreshold:   100_000, altaFacturacion:    16_000, ticketAlto:    640,  ticketBajo:    280  },
  MPE: { name: 'Perú',      currency: 'PEN', flag: '🇵🇪', salesThreshold:    10_000, altaFacturacion:     1_110, ticketAlto:     59,  ticketBajo:     26  },
};

// Umbral de visitas (sin moneda, igual para todos los sitios)
const UMBRAL_ALTA_DEMANDA_VISITAS = 100;

// Función global para formatear números
const fmtNum = (n) => new Intl.NumberFormat('en-US').format(n || 0);

// Locales por sitio para formato de moneda local
const LOCALE_BY_SITE = {
  MLA: 'es-AR', MLB: 'pt-BR', MLM: 'es-MX', MCO: 'es-CO', MLC: 'es-CL', MLU: 'es-UY', MPE: 'es-PE'
};

// Formatear en moneda local del sitio
const fmtLocal = (amount, siteId) => {
  const site = SITES[siteId];
  if (!site) return String(amount);
  const locale = LOCALE_BY_SITE[siteId] || 'es-CO';
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: site.currency,
    maximumFractionDigits: 0
  }).format(amount || 0);
};

// Fecha de creación estimada por el backend a partir del item ID
// (services/creation_date_estimator.py + services/item_age_model.py). Si no hay
// calibración ni curva para el sitio no viene estimated_created_at.
const fmtEstimatedCreated = (item, siteId) => {
  if (!item.estimated_created_at) return null;
  return new Date(item.estimated_created_at).toLocaleDateString(LOCALE_BY_SITE[siteId] || 'es-CO', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

// Oldest extension the search flow supports: 1.0.11 sends soldIsPlus ("+N
// vendidos"), without which /analyze over-scales sold quantities. Publish the
// extension version on the Web Store BEFORE raising this.
const MIN_EXTENSION_VERSION = process.env.REACT_APP_MIN_EXTENSION_VERSION || '1.0.14';

// Umbral de negocio por sitio, con fallback a Colombia si el sitio no lo define
const getSiteThreshold = (siteId, key) => SITES[siteId]?.[key] ?? SITES.MCO[key];

// /expand-search runs async on the ai-worker (Gemini): the POST only returns a
// job_id, so the real result has to be polled from job-status until the worker
// marks it completed/error. /analyze is synchronous (pure math in the backend).
const pollJob = async (statusUrl, { intervalMs = 3500, maxWaitMs = 60000, onStep } = {}) => {
  const deadline = Date.now() + maxWaitMs;
  while (Date.now() < deadline) {
    let data;
    try {
      ({ data } = await apiClient.get(statusUrl));
    } catch (e) {
      // A dropped poll (network blip) must not abandon a long job; only a
      // real HTTP answer (e.g. 404 job not found) does.
      if (e.response) throw e;
      await new Promise((resolve) => setTimeout(resolve, intervalMs));
      continue;
    }
    if (data.status === 'completed') return data.result;
    if (data.step && onStep) onStep(data.step);
    if (data.status === 'error') throw new Error(data.error || 'El trabajo falló');
    await new Promise((resolve) => setTimeout(resolve, intervalMs));
  }
  throw new Error('Tiempo de espera agotado');
};

export default function ProductResearch() {
  const navigate = useNavigate();
  const [extensionStatus, setExtensionStatus] = useState({ checking: true, installed: false, hasPermissions: false });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSite, setSelectedSite] = useState('MCO');
  const [isSearching, setIsSearching] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('demanda');
  const [error, setError] = useState(null);
  const [suggestedQueries, setSuggestedQueries] = useState([]);
  const [searchCredits, setSearchCredits] = useState(null);

  const fetchSearchCredits = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/api/research/credits');
      setSearchCredits(data);
    } catch (e) {
      // Non-critical — the label just won't show
    }
  }, []);

  useEffect(() => {
    fetchSearchCredits();
  }, [fetchSearchCredits]);

  // Function to check extension status
  const checkExtensionAndSetup = useCallback(async () => {
    setExtensionStatus({ checking: true, installed: false, hasPermissions: false });
    
    // Wait a bit for content script
    await new Promise(resolve => setTimeout(resolve, 500));
    
    const status = await checkExtensionInstalled();
    
    if (status.installed) {
      const version = status.version || '1.0.0';

      setExtensionStatus({
        checking: false,
        installed: true,
        version: version,
        hasPermissions: isExtensionVersionAtLeast(version, MIN_EXTENSION_VERSION)
      });

      // Get user's site from backend
      try {
        const response = await apiClient.get('/api/research/token');
        if (response.data.siteId) {
          setSelectedSite(response.data.siteId);
        }
      } catch (e) {
        console.log('[Research] Could not get user site');
      }
    } else {
      setExtensionStatus({ checking: false, installed: false, hasPermissions: false });
    }
  }, []);

  // Alias for retry button
  const setupToken = checkExtensionAndSetup;

  // Check extension on mount
  useEffect(() => {
    checkExtensionAndSetup();
  }, [checkExtensionAndSetup]);

  // Calculate metrics from results
  const calculateAndSetMetrics = useCallback((data) => {
    if (!data || !data.topItems) return;

    const items = data.topItems;
    const visits = data.visits || {};
    const analysis = data.analysis;

    if (analysis) {
      data.demand = {
        totalVisits: analysis.total_visits || 0,
        avgVisits: analysis.avg_visits || 0,
        totalVentasBrutas: analysis.total_ventas_brutas || 0,
        level: analysis.signals?.demand?.signal || 'unknown'
      };

      data.market = {
        totalResults: analysis.total_results || 0,
        platinumPct: analysis.platinum_percentage || 0,
        goldPct: analysis.gold_percentage || 0,
        fullPct: analysis.full_percentage || 0,
        catalogPct: analysis.catalog_percentage || 0,
        sellerCounts: analysis.seller_counts || {}
      };

      data.strategy = {
        signals: analysis.signals || {},
        recommendation: analysis.recommendation || 'caution',
        recommendationMessage: analysis.recommendation_message || ''
      };
    } else {
      const totalVisits = Object.values(visits).reduce((a, b) => a + (b || 0), 0);
      const avgVisits = items.length > 0 ? Math.round(totalVisits / Math.min(items.length, 10)) : 0;

      data.demand = { totalVisits, avgVisits, level: totalVisits > 1000 ? 'high' : 'low' };
      data.market = { totalResults: data.search?.total || 0 };
      data.strategy = { recommendation: 'caution' };
    }
  }, []);

  // Handle search
  const handleSearch = useCallback(async () => {
    if (!searchQuery.trim()) {
      toast.error('Ingresa un término de búsqueda');
      return;
    }
    
    if (!extensionStatus.installed) {
      toast.error('Instala la extensión MercadoPro - Investigación');
      return;
    }
    
    if (!extensionStatus.hasPermissions) {
      toast.error(`La extensión necesita actualizarse a la versión ${MIN_EXTENSION_VERSION} o superior.`);
      return;
    }
    
    setIsSearching(true);
    setError(null);
    setResults(null);
    setSuggestedQueries([]);

    try {
      const authToken = localStorage.getItem('authToken');
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const siteConfig = SITES[selectedSite];

      // PASO 1: Gemini genera variantes de búsqueda (normalmente 20-45s). Sin
      // variantes la búsqueda NO continúa: es preferible esperar (el ai-worker
      // reintenta) o que el usuario reintente a escanear solo su término.
      // Aún no se consume crédito (eso ocurre en /analyze).
      const expandToastId = toast.loading('Generando variantes de búsqueda con IA...');
      let aiQueries = [];
      try {
        const { data: expandStart } = await apiClient.post('/api/research/expand-search', {
          query: searchQuery.trim(),
          siteId: selectedSite
        });
        const expandResult = await pollJob(
          `/api/research/expand-search/job-status/${expandStart.job_id}`,
          // Covers the ai-worker's worst case (retries × 2 Gemini models × 60s).
          {
            intervalMs: 2000,
            maxWaitMs: 300000,
            onStep: (step) => toast.loading(step, { id: expandToastId }),
          }
        );
        aiQueries = expandResult?.suggestedQueries || [];
      } catch (e) {
        console.warn('[Research] Could not get AI suggestions:', e);
      }
      if (aiQueries.length === 0) {
        toast.dismiss(expandToastId);
        throw new Error('No se pudieron generar las variantes de búsqueda con IA. Intenta de nuevo en unos minutos (no se consumió ningún crédito).');
      }
      setSuggestedQueries(aiQueries);
      toast.success(`IA generó ${aiQueries.length} variantes: ${aiQueries.join(', ')}`, { id: expandToastId, duration: 4000 });

      // PASO 2: La extensión busca en MeLi con todos los términos a la vez (hasta 4)
      const searchTerms = [searchQuery.trim(), ...aiQueries].slice(0, 4);
      toast.info(`Buscando en MeLi ${SITES[selectedSite].flag} (${searchTerms.length} términos, abriendo pestañas)...`);

      console.log('[Research] Running extension batch search for searchTerms:', searchTerms);

      const extResult = await sendToExtension('searchProducts', {
        searchTerms,
        siteId: selectedSite,
        backendUrl,
        authToken
      });

      console.log('[Research] Extension result:', extResult?.success);

      if (!extResult?.success) {
        if (extResult?.error_code === 'NO_MELI_TOKEN') {
          toast.error('No se pudo obtener el token de MercadoLibre. Verifica tu sesión.', { duration: 8000 });
          throw new Error('No se pudo obtener el token de MercadoLibre');
        }
        throw new Error(extResult?.error || 'Error desconocido en la extensión');
      }

      const extData = extResult.data || {};

      // PASO 3: Deduplicar por item ID y mapear campos al formato del backend
      const seenItemIds = new Set();
      const mergedItems = [];
      for (const item of (extData.items || [])) {
        if (!seenItemIds.has(item.itemId)) {
          seenItemIds.add(item.itemId);
          mergedItems.push({
            id: item.itemId,
            title: item.title || '',
            price: item.price || 0,
            visits_30d: item.visits30d || 0,
            // Extension ≥1.0.8 scrapes lifetime units sold; the backend
            // simulates visits_30d from it + the estimated creation date.
            sold_quantity: item.soldQuantity ?? null,
            // Extension ≥1.0.11: whether the label was bucketed ("+500
            // vendidos"); only then is sold_quantity scaled. Older versions
            // don't send it (null) and keep the old always-scaled behavior.
            sold_is_plus: item.soldIsPlus ?? null,
            // Extension ≥1.0.9: newest same-site review dates (recent-sales
            // signal) — { count30d, windowCovered, dates, oldestDate, ... } or null.
            reviews: item.reviews ?? null,
            is_full: item.isFull || false,
            catalog_listing: item.isCatalog || false,
            is_international: item.isInternational || false,
            origin_country: item.originCountry || null,
            permalink: item.permalink || '',
            thumbnail: item.thumbnail || null,
          });
        }
      }

      console.log(`[Research] Merged: ${mergedItems.length} unique items from extension`);
      toast.info(`${mergedItems.length} productos únicos encontrados, analizando el mercado...`, { duration: 2000 });

      // PASO 4: Analizar el pool combinado con el backend
      // totalSearchResults es el contador real de MeLi para el término
      // original del usuario (ej. "+9.999 resultados"), no la cantidad que
      // la extensión analizó — así el tamaño del mercado tiene sentido.
      const { data: analysisResult } = await apiClient.post('/api/research/analyze', {
        query: searchQuery.trim(),
        siteId: selectedSite,
        totalResults: extData.totalSearchResults ?? mergedItems.length,
        items: mergedItems,
        salesThreshold: siteConfig?.salesThreshold || 5000000
      });

      // El backend descontó un crédito de búsqueda al aceptar /analyze.
      fetchSearchCredits();

      console.log('[Research] Analysis result:', analysisResult);

      if (analysisResult?.success) {
        // Keep natural extraction order — user can sort via column headers in the table
        const processedItems = analysisResult.items || mergedItems;
        const analysis = analysisResult.analysis;

        const formattedData = {
          query: searchQuery.trim(),
          siteId: selectedSite,
          search: {
            total: extData.totalSearchResults ?? mergedItems.length,
            resultCount: processedItems.length
          },
          topItems: processedItems,
          visits: {},
          analysis: analysis
        };

        processedItems.forEach(item => {
          if (item.visits_30d !== undefined) {
            formattedData.visits[item.id] = item.visits_30d;
          }
        });

        setResults(formattedData);
        calculateAndSetMetrics(formattedData);
        toast.success(`¡Análisis completo! ${processedItems.length} productos analizados`);
        saveAnalysis(formattedData);
      } else {
        throw new Error('Error en análisis del backend');
      }

    } catch (err) {
      console.error('[Research] Search error:', err);
      if (err.response?.status === 429) {
        const message = err.response?.data?.detail || 'Alcanzaste el límite de búsquedas de tu plan.';
        setError(message);
        toast.error(message, { duration: 8000 });
        fetchSearchCredits();
      } else if (err.message === 'EXTENSION_RELOADED') {
        const msg = 'La extensión fue actualizada. Recarga la página para continuar.';
        setError(msg);
        toast.error(msg, {
          duration: 10000,
          action: { label: 'Recargar', onClick: () => window.location.reload() }
        });
      } else {
        setError(err.message);
        toast.error(`Error: ${err.message}`);
      }
    } finally {
      setIsSearching(false);
    }
  }, [searchQuery, selectedSite, extensionStatus, calculateAndSetMetrics, fetchSearchCredits]);

  // Save analysis to backend
  const saveAnalysis = async (data) => {
    try {
      await apiClient.post('/api/research/save-analysis', {
        query: data.query,
        siteId: data.siteId,
        totalResults: data.search?.total || 0,
        itemsAnalyzed: data.topItems?.length || 0,
        demandLevel: data.demand?.level,
        competitionLevel: data.market?.level,
        recommendation: data.strategy?.recommendation?.type
      });
    } catch (err) {
      console.error('[Research] Failed to save analysis:', err);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !isSearching) {
      handleSearch();
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Header */}
      <div className="border-b border-border bg-card/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/dashboard')} className="p-2 hover:bg-muted rounded-lg">
                <ArrowLeft className="w-5 h-5 text-muted-foreground" />
              </button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-3">
                  <Target className="w-7 h-7 text-blue-600 dark:text-blue-400" />
                  Búsqueda de Productos
                </h1>
                <p className="text-muted-foreground text-sm mt-1">Analiza nichos en Mercado Libre</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <TutorialVideoButton video="Busqueda de productos.mp4" caption="Cómo buscar y analizar productos." />
              <ExtensionStatus status={extensionStatus} onRetry={setupToken} />
              {extensionStatus.installed && (
                <button
                  onClick={downloadExtensionLogs}
                  title="Descargar logs de la extensión"
                  className="p-2 hover:bg-muted rounded-lg text-muted-foreground/70 hover:text-muted-foreground transition-colors"
                >
                  <Download className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Extension not installed warning */}
        {!extensionStatus.checking && !extensionStatus.installed && (
          <ExtensionInstallPrompt />
        )}
        
        {/* Extension needs update warning */}
        {!extensionStatus.checking && extensionStatus.installed && !extensionStatus.hasPermissions && (
          <ExtensionUpdatePrompt currentVersion={extensionStatus.version} onRecheck={checkExtensionAndSetup} />
        )}

        {/* Search Bar */}
        <div className="bg-popover rounded-2xl p-6 mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <select
              value={selectedSite}
              onChange={(e) => setSelectedSite(e.target.value)}
              className="px-4 py-3 bg-muted border border-border rounded-xl text-foreground focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={isSearching}
            >
              {Object.entries(SITES).map(([code, site]) => (
                <option key={code} value={code}>{site.flag} {site.name}</option>
              ))}
            </select>
            
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Buscar producto o nicho (ej: auriculares bluetooth)"
                className="w-full pl-12 pr-4 py-3 bg-muted border border-border rounded-xl text-foreground placeholder-muted-foreground/70 focus:outline-none focus:ring-2 focus:ring-blue-500"
                disabled={isSearching || !extensionStatus.installed}
                autoComplete="off"
                data-gramm="false"
                data-gramm_editor="false"
                data-enable-grammarly="false"
                data-lpignore="true"
                data-1p-ignore="true"
                data-bwignore="true"
                data-testid="research-search-input"
              />
            </div>
            
            <button
              onClick={handleSearch}
              disabled={isSearching || !extensionStatus.installed || (searchCredits && !searchCredits.unlimited && searchCredits.remaining <= 0)}
              data-testid="research-search-button"
              className="px-8 py-3 bg-blue-600 hover:bg-blue-500 disabled:bg-muted disabled:cursor-not-allowed rounded-xl font-semibold flex items-center justify-center gap-2 transition-colors text-white"
            >
              {isSearching ? (
                <><Loader2 className="w-5 h-5 animate-spin" /> Analizando...</>
              ) : (
                <><Search className="w-5 h-5" /> Analizar</>
              )}
            </button>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-2 mt-3">
            <p className="text-xs text-muted-foreground/70">
              Introduce el nombre del producto que quieres investigar, idealmente usando 2-3 palabras. Ejemplo: "reloj digital".
            </p>
            <CreditsBadge credits={searchCredits} label="Búsquedas" data-testid="search-credits-badge" />
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-8 flex items-start gap-3">
            <XCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-700 dark:text-red-300 font-medium">Error en la búsqueda</p>
              <p className="text-red-600/80 dark:text-red-400/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className="space-y-6" data-testid="research-results">
            {suggestedQueries.length > 0 && (
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-muted-foreground/70 flex items-center gap-1.5">
                  <Search className="w-3.5 h-3.5" />
                  Búsquedas IA:
                </span>
                {suggestedQueries.map((q, i) => (
                  <span key={i} className="px-2.5 py-1 bg-blue-500/10 border border-blue-500/20 text-blue-700 dark:text-blue-300 rounded-full">
                    {q}
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2 bg-popover p-1.5 rounded-xl w-fit">
              {[
                { id: 'demanda', label: 'Demanda', icon: TrendingUp },
                { id: 'mercado', label: 'Mercado', icon: Users },
                { id: 'estrategia', label: 'Estrategia', icon: Target }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-5 py-2.5 rounded-lg font-medium flex items-center gap-2 transition-colors ${
                    activeTab === tab.id ? 'bg-blue-600 text-white' : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'demanda' && <DemandTab results={results} siteConfig={{...SITES[selectedSite], siteId: selectedSite}} />}
            {activeTab === 'mercado' && <MarketTab results={results} siteConfig={{...SITES[selectedSite], siteId: selectedSite}} />}
            {activeTab === 'estrategia' && <StrategyTab results={results} siteConfig={{...SITES[selectedSite], siteId: selectedSite}} />}
          </div>
        )}

        {/* Empty state */}
        {!results && !isSearching && !error && extensionStatus.installed && (
          <div className="text-center py-16">
            <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-6">
              <Search className="w-10 h-10 text-muted-foreground/70" />
            </div>
            <h3 className="text-xl font-semibold text-muted-foreground mb-2">Comienza tu investigación</h3>
            <p className="text-muted-foreground/70 max-w-md mx-auto">
              Ingresa un producto o nicho para analizar demanda, competencia y oportunidades.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// Extension Status Component
// Thumbnail with referrerPolicy bypass + fallback icon when null or broken
function ItemThumb({ src, size = 'w-12 h-12' }) {
  const [broken, setBroken] = React.useState(false);
  if (!src || broken) {
    return (
      <div className={`${size} rounded-lg bg-muted flex items-center justify-center flex-shrink-0`}>
        <ImageIcon className="w-5 h-5 text-muted-foreground/70" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt=""
      referrerPolicy="no-referrer"
      onError={() => setBroken(true)}
      className={`${size} rounded-lg object-cover bg-muted flex-shrink-0`}
    />
  );
}

function ExtensionStatus({ status, onRetry }) {
  if (status.checking) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-muted rounded-full">
        <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Verificando...</span>
      </div>
    );
  }
  
  if (!status.installed) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full">
        <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400" />
        <span className="text-sm text-amber-700 dark:text-amber-300">Extensión no detectada</span>
      </div>
    );
  }
  
  // Extensión instalada pero sin permisos correctos
  if (!status.hasPermissions) {
    return (
      <div className="flex items-center gap-2 px-4 py-2 bg-orange-500/10 border border-orange-500/30 rounded-full">
        <AlertTriangle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
        <span className="text-sm text-orange-700 dark:text-orange-300">Actualización requerida (v{MIN_EXTENSION_VERSION})</span>
      </div>
    );
  }
  
  return (
    <div className="flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full">
      <CheckCircle2 className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
      <span className="text-sm text-emerald-700 dark:text-emerald-300">Conectado v{status.version}</span>
    </div>
  );
}

// Official Chrome Web Store listing for the extension
const CHROME_WEBSTORE_URL = 'https://chromewebstore.google.com/detail/emmahjdagopfnjhabieadgfchenlfcak?utm_source=item-share-cb';

// Extension Install Prompt
function ExtensionInstallPrompt() {
  return (
    <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-6 mb-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-amber-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <Download className="w-6 h-6 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">Instala La Extension Oficial De Selltrix</h3>
          <p className="text-amber-700/80 dark:text-amber-300/80 text-sm mb-4">
            Para usar este módulo necesitas instalar la extensión Selltrix en Chrome.
          </p>
          <a
            href={CHROME_WEBSTORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 text-white rounded-xl font-medium transition-colors mb-4"
          >
            <ExternalLink className="w-5 h-5" /> Instalar Extensión
          </a>
          <div className="bg-card/60 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Instalación:</p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Haz clic en "Instalar Extensión" para abrir la Chrome Web Store</li>
              <li>Haz clic en el botón "Agregar a Chrome"</li>
              <li>Confirma haciendo clic en "Agregar extensión" en el cuadro de diálogo</li>
              <li>Recarga esta página</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

function ExtensionUpdatePrompt({ currentVersion, onRecheck }) {
  const [updating, setUpdating] = useState(false);

  // Asks the extension to pull the new version from the Web Store right now
  // (extension ≥ the version that added MP_EXT_UPDATE_CHECK); older installs
  // don't answer and must follow the manual steps below.
  const handleUpdate = async () => {
    setUpdating(true);
    const result = await requestExtensionUpdate();
    console.log('[Research] Extension update check:', result);
    // The extension can reload into the new version before its reply gets
    // out, which surfaces as a dropped message port — that's success too.
    const reloadedMidReply = result.status === 'error' &&
      /port closed|context invalidated|receiving end/i.test(result.error || '');
    // 'checking' = the Store found an update and Chrome is still downloading it.
    // 'unsupported' = no reply at all: either a version without this message or
    // an older one stuck in requestUpdateCheck while the download runs — so
    // still wait for a newer version before falling back to the manual steps.
    const updateLikely = ['update_available', 'checking'].includes(result.status) || reloadedMidReply;
    if (updateLikely || result.status === 'unsupported') {
      toast.info('Actualizando la extensión... esto puede tardar hasta un minuto.');
      // Download + swap time varies, so wait for the new version to announce
      // itself (its fresh bridge is injected into this tab) instead of
      // reloading on a fixed timer.
      for (let i = 0; i < 30; i++) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const status = await checkExtensionInstalled();
        if (status.installed && isExtensionVersionAtLeast(status.version, MIN_EXTENSION_VERSION)) {
          toast.success(`Extensión actualizada a la versión ${status.version}.`);
          onRecheck();
          return;
        }
      }
      // The bridge in this tab may be orphaned by the update (versions before
      // the re-inject fix) — a full reload picks up the new one.
      if (updateLikely) {
        window.location.reload();
        return;
      }
    }
    setUpdating(false);
    if (result.status === 'no_update') {
      toast.info('La Chrome Web Store aún no ofrece una versión más nueva. Intenta más tarde.', { duration: 8000 });
    } else if (result.status === 'throttled') {
      toast.info('Chrome limitó las comprobaciones de actualización. Intenta en unos minutos o sigue los pasos de abajo.', { duration: 8000 });
    } else {
      toast.info('Tu versión no se puede actualizar automáticamente. Sigue los pasos de abajo.', { duration: 8000 });
    }
  };

  return (
    <div className="bg-orange-500/10 border border-orange-500/30 rounded-2xl p-6 mb-8">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 bg-orange-500/20 rounded-xl flex items-center justify-center flex-shrink-0">
          <RefreshCw className="w-6 h-6 text-orange-600 dark:text-orange-400" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-orange-800 dark:text-orange-200 mb-2">Actualización Requerida</h3>
          <p className="text-orange-700/80 dark:text-orange-300/80 text-sm mb-4">
            Tienes la versión {currentVersion || 'desconocida'} de la extensión Selltrix; la búsqueda de productos
            necesita la {MIN_EXTENSION_VERSION} o superior.
          </p>
          <div className="flex flex-wrap gap-3 mb-4">
            <button
              type="button"
              onClick={handleUpdate}
              disabled={updating}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-orange-600 hover:bg-orange-500 disabled:opacity-60 text-white rounded-xl font-medium transition-colors"
            >
              {updating ? <Loader2 className="w-5 h-5 animate-spin" /> : <RefreshCw className="w-5 h-5" />} Actualizar ahora
            </button>
            <button
              type="button"
              onClick={onRecheck}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-muted hover:bg-muted/70 text-foreground rounded-xl font-medium transition-colors"
            >
              Ya actualicé, verificar
            </button>
          </div>
          <a
            href={CHROME_WEBSTORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline mb-4"
          >
            <ExternalLink className="w-4 h-4" /> Abrir en la Chrome Web Store
          </a>
          <div className="bg-card/60 rounded-xl p-4 space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Pasos para actualizar:</p>
            <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
              <li>Ve a <code className="px-1 py-0.5 bg-muted rounded text-orange-700 dark:text-orange-300">chrome://extensions/</code></li>
              <li>Activa el interruptor "Modo de desarrollador" (arriba a la derecha)</li>
              <li>Haz clic en el botón "Actualizar" para forzar la actualización de las extensiones</li>
              <li>Recarga esta página</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}

// Tab Components - NUEVO DISEÑO GLASSMORPHISM
function DemandTab({ results, siteConfig }) {
  const d = results.demand || {};
  const items = results.topItems || [];
  const siteId = siteConfig?.siteId || 'MCO';
  
  const totalVisits = d.totalVisits || 0;
  const avgVisits = items.length > 0 ? Math.round(totalVisits / items.length) : 0;
  const ventasEstimadas = Math.round(totalVisits * 0.03);
  const facturacionTotal = d.totalVentasBrutas || 0;

  // Reduce font size when the formatted local amount is very long
  const getFacturacionTextSize = (localValue) => {
    const digits = Math.floor(localValue).toString().length;
    if (digits >= 12) return 'text-base lg:text-lg';
    if (digits >= 9)  return 'text-lg lg:text-xl';
    if (digits >= 7)  return 'text-xl lg:text-2xl';
    return 'text-2xl lg:text-3xl';
  };
  
  return (
    <div className="space-y-8" data-testid="demand-panel">
      {/* Header con título - palabras separadas */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground tracking-[0.15em]">
          PANEL DE CONTROL DE RENDIMIENTO MERCADOLIBRE
        </h2>
      </div>
      
      {/* KPI Cards - Glassmorphism */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6" data-testid="kpi-grid">
        {/* Card 1: Visitas 30 Días */}
        <div 
          className="relative overflow-hidden p-4 lg:p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl shadow-xl hover:bg-muted/60 hover:border-white/20 transition-all duration-300"
          data-testid="kpi-card-visits"
        >
          <div className="relative z-10 pr-10">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Visitas 30 Días Globales</p>
            <p className="text-2xl lg:text-3xl font-bold text-foreground mt-2 truncate">{fmtNum(totalVisits)}</p>
          </div>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 lg:w-10 lg:h-10 text-blue-600/60 dark:text-blue-400/60">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z"/>
              <path d="m12 15-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z"/>
              <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0"/>
              <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5"/>
            </svg>
          </div>
        </div>
        
        {/* Card 2: Visitas Promedio */}
        <div 
          className="relative overflow-hidden p-4 lg:p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl shadow-xl hover:bg-muted/60 hover:border-white/20 transition-all duration-300"
          data-testid="kpi-card-avg-visits"
        >
          <div className="relative z-10 pr-10">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Visitas Promedio por Pub</p>
            <p className="text-2xl lg:text-3xl font-bold text-foreground mt-2 truncate">{fmtNum(avgVisits)}</p>
          </div>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 lg:w-10 lg:h-10 text-cyan-600/60 dark:text-cyan-400/60">
            <Search className="w-full h-full" />
          </div>
        </div>
        
        {/* Card 3: Ventas Estimadas */}
        <div 
          className="relative overflow-hidden p-4 lg:p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl shadow-xl hover:bg-muted/60 hover:border-white/20 transition-all duration-300"
          data-testid="kpi-card-sales"
        >
          <div className="relative z-10 pr-10">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Ventas Estimadas Global</p>
            <p className="text-2xl lg:text-3xl font-bold text-foreground mt-2 truncate">{fmtNum(ventasEstimadas)}</p>
          </div>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 lg:w-10 lg:h-10 text-emerald-600/60 dark:text-emerald-400/60">
            <ShoppingCart className="w-full h-full" />
          </div>
        </div>
        
        {/* Card 4: Facturación Estimada en USD */}
        <div 
          className="relative overflow-hidden p-4 lg:p-5 rounded-2xl border border-white/10 bg-gradient-to-br from-white/5 to-white/0 backdrop-blur-xl shadow-xl hover:bg-muted/60 hover:border-white/20 transition-all duration-300"
          data-testid="kpi-card-revenue"
        >
          <div className="relative z-10 pr-10">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Facturación Estimada ({siteConfig.currency})</p>
            <p className={`${getFacturacionTextSize(facturacionTotal)} font-bold text-foreground mt-2 truncate`}>{fmtLocal(facturacionTotal, siteId)}</p>
          </div>
          <div className="absolute right-4 top-1/2 -translate-y-1/2 w-8 h-8 lg:w-10 lg:h-10 text-amber-600/60 dark:text-amber-400/60">
            <DollarSign className="w-full h-full" />
          </div>
        </div>
      </div>
      
      {/* Análisis de Demanda - Panel Estratégico */}
      <DemandAnalysisPanel 
        items={items} 
        avgVisits={avgVisits} 
        facturacionTotal={facturacionTotal}
        siteId={siteId}
      />
      
      {/* Publicaciones - Lista con Glassmorphism */}
      <PublicationsTable items={items} siteId={siteId} />
    </div>
  );
}

// Tabla de publicaciones con columnas ordenables
function PublicationsTable({ items, siteId }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('desc');

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const sortedItems = sortKey
    ? [...items].sort((a, b) => {
        const av = a[sortKey] ?? (typeof a[sortKey] === 'boolean' ? false : 0);
        const bv = b[sortKey] ?? (typeof b[sortKey] === 'boolean' ? false : 0);
        if (typeof av === 'boolean' || sortKey === 'catalog_listing' || sortKey === 'is_full') {
          const an = av ? 1 : 0;
          const bn = bv ? 1 : 0;
          return sortDir === 'desc' ? bn - an : an - bn;
        }
        if (typeof av === 'string') {
          return sortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
        }
        return sortDir === 'desc' ? bv - av : av - bv;
      })
    : items;

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3 h-3 opacity-30" />;
    return sortDir === 'desc'
      ? <ArrowDown className="w-3 h-3 text-blue-600 dark:text-blue-400" />
      : <ArrowUp className="w-3 h-3 text-blue-600 dark:text-blue-400" />;
  };

  const ColHeader = ({ col, label, className = '' }) => (
    <button
      onClick={() => handleSort(col)}
      className={`flex items-center gap-1 uppercase tracking-wide hover:text-foreground transition-colors ${sortKey === col ? 'text-blue-600 dark:text-blue-400' : 'text-muted-foreground/70'} ${className}`}
    >
      {label}
      <SortIcon col={col} />
    </button>
  );

  return (
    <div data-testid="publications-section">
      <h3 className="text-xl font-bold text-foreground mb-6 flex items-center gap-3">
        <Package className="w-6 h-6 text-blue-600 dark:text-blue-400" />
        Publicaciones
        <span className="text-sm font-normal text-muted-foreground/70">({sortedItems.length})</span>
      </h3>

      {/* Header de columnas ordenables */}
      <div className="hidden lg:grid grid-cols-12 gap-4 px-4 pb-3 text-xs font-medium">
        <div className="col-span-3">
          <ColHeader col="title" label="Producto" />
        </div>
        <div className="col-span-1 flex justify-center">
          <ColHeader col="visits_30d" label="Visitas" />
        </div>
        <div className="col-span-1 flex justify-center">
          <ColHeader col="age_days" label="Creada" />
        </div>
        <div className="col-span-2 flex justify-center">
          <ColHeader col="price" label="Precio" />
        </div>
        <div className="col-span-2 flex justify-center">
          <ColHeader col="ventas_brutas_estimadas" label="Facturación Est." />
        </div>
        <div className="col-span-1 flex justify-center">
          <ColHeader col="catalog_listing" label="Catálogo" />
        </div>
        <div className="col-span-1 flex justify-center">
          <ColHeader col="is_full" label="Full" />
        </div>
        <div className="col-span-1 flex justify-center">
          <ColHeader col="is_international" label="Vendedor" />
        </div>
      </div>

      <div className="space-y-3">
        {sortedItems.map((item, i) => (
          <div
            key={item.id || item.permalink || item.title}
            className="grid grid-cols-12 gap-4 items-center p-4 rounded-xl bg-popover/40 border border-white/5 hover:bg-muted/60 hover:border-white/10 transition-all duration-300 backdrop-blur-sm"
            data-testid={`publication-row-${i}`}
          >
            {/* Imagen + Nombre */}
            <div className="col-span-12 lg:col-span-3 flex items-center gap-4">
              <ItemThumb src={item.thumbnail} size="w-12 h-12" />
              <div className="min-w-0 flex-1">
                <a
                  href={item.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-foreground hover:text-blue-600 dark:hover:text-blue-400 line-clamp-2 transition-colors"
                >
                  {item.title}
                </a>
              </div>
            </div>

            {/* Visitas */}
            <div className="col-span-4 lg:col-span-1 text-center">
              <p className="text-xs text-muted-foreground/70 uppercase tracking-wide lg:hidden mb-1">Visitas</p>
              <p className="text-base font-semibold text-blue-600 dark:text-blue-400">{fmtNum(item.visits_30d || 0)}</p>
            </div>

            {/* Fecha de creación estimada (modelo item ID → fecha del backend) */}
            <div className="col-span-6 lg:col-span-1 text-center order-last lg:order-none">
              <p className="text-xs text-muted-foreground/70 uppercase tracking-wide lg:hidden mb-1">Creada</p>
              {fmtEstimatedCreated(item, siteId) ? (
                <p
                  className="text-sm font-medium text-foreground"
                  title={`Fecha estimada a partir del ID de la publicación (~${fmtNum(item.age_days)} días${item.created_margin_days ? `, ±${item.created_margin_days} d` : ''})`}
                >
                  {fmtEstimatedCreated(item, siteId)}
                </p>
              ) : (
                <p className="text-sm text-muted-foreground/70" title="Sin datos suficientes para estimar la fecha">—</p>
              )}
            </div>

            {/* Precio en moneda local */}
            <div className="col-span-4 lg:col-span-2 text-center">
              <p className="text-xs text-muted-foreground/70 uppercase tracking-wide lg:hidden mb-1">Precio</p>
              <p className="text-base font-semibold text-foreground">{fmtLocal(item.price, siteId)}</p>
            </div>

            {/* Facturación Estimada en moneda local */}
            <div className="col-span-4 lg:col-span-2 text-center">
              <p className="text-xs text-muted-foreground/70 uppercase tracking-wide lg:hidden mb-1">Fact. Est.</p>
              <p className="text-base font-bold text-emerald-600 dark:text-emerald-400">{fmtLocal(item.ventas_brutas_estimadas || 0, siteId)}</p>
            </div>

            {/* Catálogo */}
            <div className="col-span-6 lg:col-span-1 flex justify-center">
              {item.catalog_listing ? (
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Catálogo</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted border border-border/50">
                  <span className="text-xs font-medium text-muted-foreground/70">Catálogo</span>
                </span>
              )}
            </div>

            {/* Full */}
            <div className="col-span-6 lg:col-span-1 flex justify-center">
              {item.is_full ? (
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-500/10 border border-emerald-500/20">
                  <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  <span className="text-xs font-medium text-emerald-600 dark:text-emerald-400">Full</span>
                  <svg className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted border border-border/50">
                  <span className="text-xs font-medium text-muted-foreground/70">No Full</span>
                </span>
              )}
            </div>

            {/* Internacional / Cross Border Trade */}
            <div className="col-span-6 lg:col-span-1 flex justify-center">
              {item.is_international ? (
                <span
                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-amber-500/10 border border-amber-500/20"
                  title='Vendedor internacional'
                >
                  <span className="text-xs font-medium text-amber-600 dark:text-amber-400">🌐 Internacional</span>
                </span>
              ) : (
                <span className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-muted border border-border/50">
                  <span className="text-xs font-medium text-muted-foreground/70">Local</span>
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {items.length === 0 && (
        <div className="text-center py-12 text-muted-foreground/70">
          <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>No hay publicaciones para mostrar</p>
        </div>
      )}
    </div>
  );
}

// Componente de Análisis de Demanda Estratégico
function DemandAnalysisPanel({ items, avgVisits, facturacionTotal, siteId }) {
  const UMBRAL_ALTA_FACTURACION = getSiteThreshold(siteId, 'altaFacturacion');

  // Publicaciones con alta demanda (>200 visitas)
  const pubsAltaDemanda = items.filter(i => (i.visits_30d || 0) > UMBRAL_ALTA_DEMANDA_VISITAS).length;

  // Publicaciones con alta facturación (umbral en moneda local del sitio)
  const pubsAltaFacturacion = items.filter(i =>
    (i.ventas_brutas_estimadas || 0) > UMBRAL_ALTA_FACTURACION
  ).length;

  // Concentración del Top 3 (todo en moneda local, ratio correcto)
  const sortedByRevenue = [...items].sort((a, b) => (b.ventas_brutas_estimadas || 0) - (a.ventas_brutas_estimadas || 0));
  const top3Revenue = sortedByRevenue.slice(0, 3).reduce((sum, i) => sum + (i.ventas_brutas_estimadas || 0), 0);
  const pctFacturacionTop3 = facturacionTotal > 0 ? Math.round((top3Revenue / facturacionTotal) * 100) : 0;

  // ========== REGLAS DE NEGOCIO ==========

  // REGLA 1: Evaluación de Competencia
  let tipoMercado = '';
  let analisisCompetencia = '';

  if (pubsAltaFacturacion === 0) {
    tipoMercado = 'sin_profundidad';
    analisisCompetencia = `El nicho carece de profundidad comercial. Ninguna publicación supera los ${fmtLocal(UMBRAL_ALTA_FACTURACION, siteId)} de facturación. Mercado no validado.`;
  } else if (pubsAltaFacturacion >= 1 && pubsAltaFacturacion <= 3) {
    if (pctFacturacionTop3 < 60) {
      tipoMercado = 'aprobado_condicional';
      analisisCompetencia = `Revisar producto de la competencia para buscar mejoras.`;
    } else {
      tipoMercado = 'lideres_aislados';
      analisisCompetencia = `Mercado de Competencia Débil con Líderes Aislados. Solo ${pubsAltaFacturacion} publicación(es) concentran el ${pctFacturacionTop3}% de la facturación. Estrategia clara: auditar a esos ${pubsAltaFacturacion} líder(es), igualar su propuesta y capturar su cuota.`;
    }
  } else if (pubsAltaFacturacion >= 4) {
    tipoMercado = 'sano_distribuido';
    analisisCompetencia = `Mercado Sano y Distribuido. ${pubsAltaFacturacion} publicaciones superan ${fmtLocal(UMBRAL_ALTA_FACTURACION, siteId)}. Hay dinero para varios jugadores y la demanda está validada.`;
  } else {
    tipoMercado = 'transicion';
    analisisCompetencia = `Mercado en transición. ${pubsAltaFacturacion} publicaciones con buena facturación y ${pctFacturacionTop3}% concentrado en el Top 3.`;
  }

  // REGLA 2: Evaluación de Tráfico
  let analisisTrafico = '';

  if (avgVisits < 100 && pubsAltaFacturacion >= SITES[siteId].altaFacturacion) {
    analisisTrafico = 'A pesar del promedio, hay publicaciones con buen nivel de facturación, que se les puede competir';
  } else if (avgVisits >= 200) {
    analisisTrafico = `Nicho con demanda. Tráfico orgánico alto.`;
  } else if (avgVisits < 100 && pubsAltaDemanda <= 1) {
    analisisTrafico = 'Tráfico limitado. El nicho no tiene suficiente demanda';
  } else {
    analisisTrafico = `Tráfico moderado. ${fmtNum(avgVisits)} visitas promedio con ${pubsAltaDemanda} publicaciones de alta demanda. Requiere diferenciación.`;
  }
  
  // VEREDICTO FINAL
  let veredicto = '';
  let aprobado = false;
  
  if (tipoMercado === 'sin_profundidad' || (avgVisits < 100 && pubsAltaDemanda <= 1)) {
    veredicto = '❌ NICHO RECHAZADO. Sin volumen de facturación ni tráfico suficiente para competir rentablemente.';
    aprobado = false;
  } else if (tipoMercado === 'lideres_aislados' || tipoMercado === 'aprobado_condicional') {
    veredicto = `✅ NICHO APROBADO. Ataca directamente a los ${pubsAltaFacturacion} líder(es) del mercado. Diferencia el producto, mejora sus fotos y activa Full + cuotas.`;
    aprobado = true;
  } else if (tipoMercado === 'sano_distribuido' && avgVisits >= 100) {
    veredicto = '✅ NICHO APROBADO. Mercado validado con múltiples ganadores. Entra con diferenciación visual y logística Full para capturar tu porción.';
    aprobado = true;
  } else if (avgVisits < 100 && facturacionTotal >= SITES[siteId].altaFacturacion) {
    veredicto = '🔍 Evaluar nicho.';
    aprobado = true;
  } else {
    veredicto = '⚠️ NICHO CONDICIONAL. Hay potencial pero requiere estrategia específica y diferenciación clara para ser rentable.';
    aprobado = false;
  }

  // Si no hay datos suficientes
  if (items.length === 0) {
    return (
      <div 
        className="w-full rounded-2xl border border-border/50 bg-popover/40 backdrop-blur-sm p-6"
        data-testid="demand-analysis-panel"
      >
        <div className="text-center py-8">
          <TrendingUp className="w-12 h-12 text-muted-foreground/70 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-muted-foreground">Análisis de Demanda</h3>
          <p className="text-sm text-muted-foreground/70 mt-2">Realiza una búsqueda para obtener el análisis estratégico del nicho.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="w-full rounded-2xl border border-blue-500/30 bg-gradient-to-br from-card to-blue-50 dark:from-slate-900/80 dark:to-blue-950/30 backdrop-blur-sm p-6 shadow-[0_0_25px_rgba(70,96,245,0.1)]"
      data-testid="demand-analysis-panel"
    >
      <h3 className="text-lg font-bold text-foreground mb-5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-blue-500/20 flex items-center justify-center">
          <TrendingUp className="w-5 h-5 text-blue-600 dark:text-blue-400" />
        </div>
        Análisis de Demanda
      </h3>
      
      {/* Métricas rápidas */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase">Visitas Prom.</p>
          <p className="text-lg font-bold text-blue-600 dark:text-blue-400">{fmtNum(avgVisits)}</p>
        </div>
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase">Pubs Alta Demanda</p>
          <p className="text-lg font-bold text-cyan-600 dark:text-cyan-400">{pubsAltaDemanda}</p>
        </div>
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase">Pubs Alta Fact.</p>
          <p className="text-lg font-bold text-emerald-600 dark:text-emerald-400">{pubsAltaFacturacion}</p>
        </div>
        <div className="bg-muted rounded-lg p-3 text-center">
          <p className="text-xs text-muted-foreground uppercase">% Top 3</p>
          <p className="text-lg font-bold text-amber-600 dark:text-amber-400">{pctFacturacionTop3}%</p>
        </div>
      </div>
      
      {/* Análisis Estratégico */}
      <div className="space-y-5">
        {/* Potencial de Tráfico y Facturación */}
        <div>
          <h4 className="text-sm font-semibold text-blue-700 dark:text-blue-300 uppercase tracking-wider mb-2 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            Potencial de Tráfico y Facturación
          </h4>
          <div className="bg-muted/30 rounded-lg p-4 border-l-2 border-blue-500/50">
            <p className="text-sm text-muted-foreground leading-relaxed mb-3">{analisisTrafico}</p>
            <p className="text-sm text-muted-foreground leading-relaxed">{analisisCompetencia}</p>
          </div>
        </div>
        
        {/* Veredicto y Plan de Ataque */}
        <div>
          <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider mb-2 flex items-center gap-2">
            <Target className="w-4 h-4" />
            Veredicto y Plan de Ataque
          </h4>
          <div className={`rounded-lg p-4 border-l-2 ${aprobado ? 'bg-emerald-500/10 border-emerald-500' : 'bg-amber-500/10 border-amber-500'}`}>
            <p className={`text-sm font-medium leading-relaxed ${aprobado ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300'}`}>
              {veredicto}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function MarketTab({ results, siteConfig }) {
  const m = results.market || {};
  const items = results.topItems || [];
  const siteId = siteConfig?.siteId || 'MCO';
  
  // Calcular precio promedio, min y max en USD
  const prices = items.map(i => i.price || 0).filter(p => p > 0);
  const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;

  // Calcular posesión del mercado (top productos por facturación)
  const sortedByRevenue = [...items].sort((a, b) => (b.ventas_brutas_estimadas || 0) - (a.ventas_brutas_estimadas || 0));
  const totalRevenue = sortedByRevenue.reduce((sum, item) => sum + (item.ventas_brutas_estimadas || 0), 0);
  const topProducts = sortedByRevenue.slice(0, 8).map(item => ({
    title: item.title?.substring(0, 35) + (item.title?.length > 35 ? '...' : '') || 'Producto',
    percentage: totalRevenue > 0 ? Math.round((item.ventas_brutas_estimadas || 0) / totalRevenue * 100) : 0
  }));

  // Tamaño dinámico basado en dígitos del valor local
  const getDynamicTextSize = (localValue, isPercentage = false) => {
    if (isPercentage) return 'text-2xl lg:text-3xl';
    const digits = Math.floor(localValue).toString().length;
    if (digits >= 9) return 'text-base lg:text-lg';
    if (digits >= 7) return 'text-lg lg:text-xl';
    if (digits >= 5) return 'text-xl lg:text-2xl';
    return 'text-2xl lg:text-3xl';
  };
  
  // % de vendedores internacionales sobre el total (entre más alto, mejor)
  const internationalCount = items.filter(i => i.is_international).length;
  const internationalPct = items.length > 0 ? Math.round((internationalCount / items.length) * 100) : 0;

  return (
    <div className="space-y-6" data-testid="market-panel">
      
      {/* Fila Superior - 5 Tarjetas con degradados y neón */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="market-kpi-grid">
        
        {/* Tarjeta 1: Publicaciones - Cian brillante */}
        <div 
          className="relative overflow-hidden p-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-cyan-600/10 border border-cyan-400/30 shadow-[0_0_15px_rgba(34,211,238,0.15)] hover:shadow-[0_0_25px_rgba(34,211,238,0.25)] transition-all duration-300"
          data-testid="market-card-publications"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wider">Publicaciones</p>
              <p className={`${getDynamicTextSize(m.totalResults || 0)} font-bold text-foreground mt-1`}>{fmtNum(m.totalResults || 0)}</p>
            </div>
            <Package className="w-8 h-8 text-cyan-600/70 dark:text-cyan-400/70" />
          </div>
        </div>
        
        {/* Tarjeta 2: % Full - Verde menta */}
        <div 
          className="relative overflow-hidden p-4 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-green-600/10 border border-emerald-400/30 shadow-[0_0_15px_rgba(52,211,153,0.15)] hover:shadow-[0_0_25px_rgba(52,211,153,0.25)] transition-all duration-300"
          data-testid="market-card-full"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">% Full</p>
              <p className={`${getDynamicTextSize(m.fullPct, true)} font-bold text-foreground mt-1`}>{m.fullPct || 0}%</p>
            </div>
            <Truck className="w-8 h-8 text-emerald-600/70 dark:text-emerald-400/70" />
          </div>
        </div>
        
        {/* Tarjeta 3: % Catálogo - Morado/Magenta */}
        <div 
          className="relative overflow-hidden p-4 rounded-2xl bg-gradient-to-br from-cyan-500/20 to-blue-600/10 border border-cyan-400/30 shadow-[0_0_15px_rgba(52,208,227,0.15)] hover:shadow-[0_0_25px_rgba(52,208,227,0.25)] transition-all duration-300"
          data-testid="market-card-catalog"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wider">% Catálogo</p>
              <p className={`${getDynamicTextSize(m.catalogPct, true)} font-bold text-foreground mt-1`}>{m.catalogPct || 0}%</p>
            </div>
            <Tag className="w-8 h-8 text-cyan-600/70 dark:text-cyan-400/70" />
          </div>
        </div>
        
        {/* Tarjeta 4: % Vendedores Internacionales - Azul cielo */}
        <div
          className="relative overflow-hidden p-4 rounded-2xl bg-gradient-to-br from-sky-500/20 to-blue-600/10 border border-sky-400/30 shadow-[0_0_15px_rgba(56,189,248,0.15)] hover:shadow-[0_0_25px_rgba(56,189,248,0.25)] transition-all duration-300"
          data-testid="market-card-international"
        >
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs font-semibold text-sky-700 dark:text-sky-300 uppercase tracking-wider">% Internacionales</p>
              <p className={`${getDynamicTextSize(internationalPct, true)} font-bold text-foreground mt-1`}>{internationalPct}%</p>
            </div>
            <Globe className="w-8 h-8 text-sky-600/70 dark:text-sky-400/70" />
          </div>
        </div>

        {/* Tarjeta 5: Precio Promedio en USD */}
        <div 
          className="relative overflow-hidden p-4 rounded-2xl bg-gradient-to-br from-amber-500/15 to-yellow-600/10 border border-amber-400/30 shadow-[0_0_15px_rgba(251,191,36,0.15)] hover:shadow-[0_0_25px_rgba(251,191,36,0.25)] transition-all duration-300"
          data-testid="market-card-price"
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">Precio Prom. ({siteConfig.currency})</p>
              <p className={`${getDynamicTextSize(avgPrice)} font-bold text-foreground mt-1 truncate`}>{fmtLocal(avgPrice, siteId)}</p>
              <p className="text-xs text-muted-foreground mt-1 truncate">
                Rango: {fmtLocal(minPrice, siteId)} – {fmtLocal(maxPrice, siteId)}
              </p>
            </div>
            <DollarSign className="w-8 h-8 text-amber-600/70 dark:text-amber-400/70 flex-shrink-0" />
          </div>
        </div>
      </div>
      
      {/* Panel Grande: Posesión del Mercado */}
      <div 
        className="w-full rounded-2xl bg-card/60 border border-cyan-500/30 shadow-[0_0_20px_rgba(34,211,238,0.1)] p-6"
        data-testid="market-possession-panel"
      >
        <h3 className="text-lg font-bold text-foreground mb-6 flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
            <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M20.488 9H15V3.512A9.025 9.025 0 0120.488 9z" />
            </svg>
          </div>
          Posesión del Mercado
        </h3>
        
        {/* Dos columnas de productos */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-4">
          {/* Columna Izquierda */}
          <div className="space-y-3">
            {topProducts.slice(0, 4).map((product, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded bg-muted flex items-center justify-center flex-shrink-0">
                  <Package className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate mb-1">{product.title}</p>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div 
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-700"
                      style={{ width: `${Math.min(product.percentage, 100)}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm font-bold text-cyan-600 dark:text-cyan-400 w-12 text-right">{product.percentage}%</span>
              </div>
            ))}
          </div>
          
          {/* Columna Derecha */}
          <div className="space-y-3">
            {topProducts.slice(4, 8).map((product, idx) => (
              <div key={idx} className="flex items-center gap-3">
                <div className="w-6 h-6 rounded bg-muted flex items-center justify-center flex-shrink-0">
                  <Package className="w-3.5 h-3.5 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-foreground truncate mb-1">{product.title}</p>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div 
                      className="h-full rounded-full bg-gradient-to-r from-cyan-500 to-blue-500 transition-all duration-700"
                      style={{ width: `${Math.min(product.percentage, 100)}%` }}
                    />
                  </div>
                </div>
                <span className="text-sm font-bold text-cyan-600 dark:text-cyan-400 w-12 text-right">{product.percentage}%</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Sugerencia de mercado */}
        <div className="mt-6 pt-4 border-t border-border/50">
          <p className="text-sm text-muted-foreground">
            <span className="text-cyan-600 dark:text-cyan-400 font-medium">💡 Oportunidad:</span>{' '}
            {topProducts[0]?.percentage > 30 
              ? `El ${topProducts[0]?.percentage}% de las ventas está concentrado en un solo producto. Diversifique la oferta para capturar más mercado.`
              : 'El mercado está bien distribuido. Busque diferenciación por calidad o servicio.'}
          </p>
        </div>
      </div>
      
      {/* Panel: Análisis de Mercado - Motor Estratégico */}
      <MarketAnalysisEngine
        totalPublicaciones={m.totalResults || 0}
        fullPct={m.fullPct || 0}
        catalogPct={m.catalogPct || 0}
        avgPrice={avgPrice}
        siteId={siteId}
        topProducts={topProducts}
      />
    </div>
  );
}

// Motor de Análisis de Mercado - 6 Reglas de Negocio
function MarketAnalysisEngine({ totalPublicaciones, fullPct, catalogPct, avgPrice, siteId, topProducts }) {
  
  // Calcular concentración del Top 3
  const pctTop3 = topProducts.slice(0, 3).reduce((sum, p) => sum + p.percentage, 0);
  
  // ========== REGLA 1: Por Publicaciones ==========
  let nivelCompetencia = '';
  let analisisPublicaciones = '';
  
  if (totalPublicaciones > 1000) {
    nivelCompetencia = 'alta_saturacion';
    analisisPublicaciones = 'alta saturación con más de 1,000 publicaciones activas';
  } else if (totalPublicaciones >= 500) {
    nivelCompetencia = 'moderada';
    analisisPublicaciones = 'saturación moderada (500-999 publicaciones)';
  } else {
    nivelCompetencia = 'baja';
    analisisPublicaciones = 'baja competencia con menos de 500 publicaciones';
  }
  
  // ========== REGLA 2: Por % FULL ==========
  let nivelFull = '';
  let analisisFull = '';
  let estrategiaFull = '';
  
  if (fullPct >= 80) {
    nivelFull = 'obligatorio';
    analisisFull = 'la competencia tiene excelente exposición con +80% en FULL';
    estrategiaFull = 'Entra EXCLUSIVAMENTE con inventario en FULL. Es requisito mínimo, no ventaja.';
  } else if (fullPct >= 41) {
    nivelFull = 'oportunidad_media';
    analisisFull = `posicionamiento medio de la competencia (${fullPct}% FULL)`;
    estrategiaFull = 'Activa FULL para ganar terreno sobre el 40%+ que no lo usa.';
  } else {
    nivelFull = 'oportunidad_alta';
    analisisFull = `muy poca exposición de la competencia (solo ${fullPct}% usa FULL)`;
    estrategiaFull = 'Domina el posicionamiento simplemente activando FULL. Ventaja competitiva inmediata.';
  }
  
  // ========== REGLA 3: Por % Catálogo ==========
  let nivelCatalogo = '';
  let analisisCatalogo = '';
  let estrategiaCatalogo = '';
  
  if (catalogPct >= 80) {
    nivelCatalogo = 'guerra_precios';
    analisisCatalogo = 'competencia feroz y comoditizada basada en precio';
    estrategiaCatalogo = 'Evita competir en catálogo. Crea tu propia ficha con infografías premium.';
  } else if (catalogPct >= 41) {
    nivelCatalogo = 'hibrido';
    analisisCatalogo = 'mercado híbrido con espacio para diferenciación';
    estrategiaCatalogo = 'Diferénciate con fotos de estilo de vida y descripciones que vendan beneficios. Crea tu propio catalogo con tu propia ficha de infografías';
  } else {
    nivelCatalogo = 'fragmentado';
    analisisCatalogo = 'nicho fragmentado con control total de tu oferta';
    estrategiaCatalogo = 'Escenario ideal: domina con infografías, títulos optimizados y descripciones únicas.';
  }
  
  // ========== REGLA 4: Producto ==========
  const estrategiaVariedad = 'La innovación manda. Diferencia tu producto para mejorar funcionalidad y mejorar el valor percibido.';
  
  // ========== REGLA 5: Por Precio Promedio ==========
  let nivelTicket = '';
  let analisisTicket = '';
  let estrategiaTicket = '';

  const TICKET_ALTO = getSiteThreshold(siteId, 'ticketAlto');
  const TICKET_BAJO = getSiteThreshold(siteId, 'ticketBajo');

  if (avgPrice > TICKET_ALTO) {
    nivelTicket = 'alto';
    analisisTicket = `ticket alto (${fmtLocal(avgPrice, siteId)}) que permite absorber costos de importación y buen empaque`;
    estrategiaTicket = `Rango ideal (>${fmtLocal(TICKET_ALTO, siteId)}). Invierte en empaque premium y absorbe flete sin destruir rentabilidad.`;
  } else if (avgPrice >= TICKET_BAJO) {
    nivelTicket = 'medio';
    analisisTicket = `ticket medio (${fmtLocal(avgPrice, siteId)}) que requiere fuerte persuasión visual`;
    estrategiaTicket = `Crea bundle para elevar ticket por encima de ${fmtLocal(TICKET_ALTO, siteId)} y activar envío gratis percibido.`;
  } else {
    nivelTicket = 'bajo';
    analisisTicket = `ticket muy bajo (${fmtLocal(avgPrice, siteId)}) con márgenes milimétricos`;
    estrategiaTicket = `Eleva el ticket obligatoriamente (actual <${fmtLocal(TICKET_BAJO, siteId)}). Vende packs de 2-3 unidades o incluye accesorios.`;
  }
  
  // ========== REGLA 6: Por Posesión del Mercado ==========
  let nivelConcentracion = '';
  let analisisConcentracion = '';
  
  if (pctTop3 >= 60) {
    nivelConcentracion = 'monopolizado';
    analisisConcentracion = 'Monopolizado" (≥60%) exige diferenciación versus lideres.';
  } else {
    nivelConcentracion = 'fragmentado';
    analisisConcentracion = 'Barrera de entrada baja pero competencia distribuida. Espacio para competir con varios vendedores a la vez.';
  }
  
  // ========== GENERAR VEREDICTO ==========
  let veredicto = '';
  let colorVeredicto = '';
  
  // Lógica de veredicto basada en combinación de factores
  const factoresPositivos = [
    nivelCompetencia === 'baja',
    nivelFull === 'oportunidad_alta',
    nivelCatalogo === 'fragmentado',
    nivelTicket === 'alto',
    nivelConcentracion === 'monopolizado' // paradójicamente, es oportunidad con diferenciación
  ].filter(Boolean).length;
  
  const factoresNegativos = [
    nivelCompetencia === 'alta_saturacion',
    nivelFull === 'obligatorio',
    nivelCatalogo === 'guerra_precios',
    nivelTicket === 'bajo'
  ].filter(Boolean).length;
  
  if (factoresPositivos >= 3 && factoresNegativos <= 1) {
    veredicto = 'Oportunidad clara con bajo riesgo de entrada';
    colorVeredicto = 'emerald';
  } else if (factoresNegativos >= 3) {
    veredicto = 'Nicho de alto riesgo con márgenes comprimidos';
    colorVeredicto = 'red';
  } else if (nivelCatalogo === 'guerra_precios' && nivelTicket === 'bajo') {
    veredicto = 'Trampa de márgenes: solo viable con volumen extremo';
    colorVeredicto = 'red';
  } else if (nivelFull === 'oportunidad_alta' && nivelCatalogo !== 'guerra_precios') {
    veredicto = 'Oportunidad híbrida con ventaja logística clara';
    colorVeredicto = 'amber';
  } else {
    veredicto = 'Mercado competitivo que requiere diferenciación estratégica';
    colorVeredicto = 'amber';
  }
  
  // ========== LECTURA DEL MERCADO (párrafo conectado) ==========
  const lecturaDelMercado = `El mercado presenta ${analisisPublicaciones}, con ${analisisFull}. ${catalogPct >= 80 ? 'La guerra de precios domina con ' + catalogPct + '% en catálogo, comprimiendo márgenes severamente.' : 'Hay espacio para diferenciación con solo ' + catalogPct + '% en catálogo.'} El ticket promedio de ${fmtLocal(avgPrice, siteId)} ${nivelTicket === 'bajo' ? 'obliga a elevar el valor percibido' : nivelTicket === 'alto' ? 'permite márgenes saludables' : 'requiere trabajo de persuasión visual'}. ${nivelConcentracion === 'monopolizado' ? 'El ' + pctTop3 + '% concentrado en 3 vendedores indica líderes aislados a los que atacar.' : 'La distribución fragmentada del pastel permite entrada con barrera baja.'}`;

  // Clases literales por color de veredicto (emerald/red/amber) — se escriben completas
  // (en vez de interpolar `colorVeredicto` dentro del className) para que el JIT de Tailwind
  // detecte cada clase, incluidas las variantes dark:, en ambos temas.
  const VEREDICTO_CLASSES = {
    emerald: { box: 'bg-emerald-500/10 border-emerald-500/30', label: 'text-emerald-700 dark:text-emerald-400', text: 'text-emerald-800 dark:text-emerald-300' },
    red: { box: 'bg-red-500/10 border-red-500/30', label: 'text-red-700 dark:text-red-400', text: 'text-red-800 dark:text-red-300' },
    amber: { box: 'bg-amber-500/10 border-amber-500/30', label: 'text-amber-700 dark:text-amber-400', text: 'text-amber-800 dark:text-amber-300' },
  };
  const veredictoClasses = VEREDICTO_CLASSES[colorVeredicto] || VEREDICTO_CLASSES.amber;

  // Si no hay datos
  if (totalPublicaciones === 0) {
    return (
      <div 
        className="w-full rounded-2xl bg-card/60 border border-cyan-500/20 shadow-[0_0_20px_rgba(52,208,227,0.08)] p-6"
        data-testid="market-analysis-panel"
      >
        <div className="text-center py-8">
          <Search className="w-12 h-12 text-muted-foreground/70 mx-auto mb-3" />
          <h3 className="text-lg font-semibold text-muted-foreground">Análisis de Mercado</h3>
          <p className="text-sm text-muted-foreground/70 mt-2">Realiza una búsqueda para obtener el análisis estratégico.</p>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="w-full rounded-2xl bg-gradient-to-br from-card to-cyan-50 dark:from-slate-900/80 dark:to-cyan-950/20 border border-cyan-500/30 shadow-[0_0_25px_rgba(52,208,227,0.12)] p-6"
      data-testid="market-analysis-panel"
    >
      <h3 className="text-lg font-bold text-foreground mb-5 flex items-center gap-3">
        <div className="w-8 h-8 rounded-lg bg-cyan-500/20 flex items-center justify-center">
          <Search className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
        </div>
        Análisis de Mercado
      </h3>
      
      <div className="space-y-5">
        {/* Veredicto */}
        <div className={`flex items-center gap-3 p-4 rounded-xl border ${veredictoClasses.box}`}>
          <span className="text-2xl">🔥</span>
          <div>
            <span className={`text-xs font-semibold uppercase tracking-wider ${veredictoClasses.label}`}>Veredicto</span>
            <p className={`text-base font-bold ${veredictoClasses.text}`}>{veredicto}</p>
          </div>
        </div>
        
        {/* Lectura del Mercado */}
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="text-lg">📊</span>
            <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Lectura del Mercado</span>
          </div>
          <div className="bg-muted/40 rounded-lg p-4 border-l-2 border-cyan-500/50">
            <p className="text-sm text-muted-foreground leading-relaxed">{lecturaDelMercado}</p>
          </div>
        </div>
        
        {/* Plan de Acción */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="text-lg">🎯</span>
            <span className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Plan de Acción</span>
          </div>
          <div className="space-y-2">
            <div className="flex items-start gap-3 bg-muted/30 rounded-lg p-3 border-l-2 border-cyan-500/50">
              <span className="text-cyan-600 dark:text-cyan-400 font-bold">•</span>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-cyan-700 dark:text-cyan-300">Producto: </span>
                {estrategiaVariedad}
              </p>
            </div>
            <div className="flex items-start gap-3 bg-muted/30 rounded-lg p-3 border-l-2 border-emerald-500/50">
              <span className="text-emerald-600 dark:text-emerald-400 font-bold">•</span>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-emerald-700 dark:text-emerald-300">Logística: </span>
                {estrategiaFull}
              </p>
            </div>
            <div className="flex items-start gap-3 bg-muted/30 rounded-lg p-3 border-l-2 border-amber-500/50">
              <span className="text-amber-600 dark:text-amber-400 font-bold">•</span>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-amber-700 dark:text-amber-300">Precio: </span>
                {estrategiaTicket}
              </p>
            </div>
            <div className="flex items-start gap-3 bg-muted/30 rounded-lg p-3 border-l-2 border-cyan-500/50">
              <span className="text-cyan-600 dark:text-cyan-400 font-bold">•</span>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium text-cyan-700 dark:text-cyan-300">Posicionamiento: </span>
                {estrategiaCatalogo}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function StrategyTab({ results, siteConfig }) {
  const s = results.strategy || {};
  const signals = s.signals || {};
  const d = results.demand || {};
  const m = results.market || {};
  const items = results.topItems || [];
  const siteId = siteConfig?.siteId || 'MCO';
  
  const facturacionTotal = d.totalVentasBrutas || 0;
  const avgVisits = d.avgVisits || 0;
  
  return (
    <div className="space-y-6" data-testid="strategy-panel">
      
      {/* Fila de 4 Métricas con Neón - PRIMERO */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="strategy-metrics-grid">
        
        {/* Cuadro 1: Saturación - Cian */}
        {/*<div 
          className="relative overflow-hidden p-5 rounded-2xl bg-popover/60 border border-cyan-400/40 shadow-[0_0_20px_rgba(34,211,238,0.15)] hover:shadow-[0_0_30px_rgba(34,211,238,0.25)] transition-all duration-300"
          data-testid="strategy-card-saturation"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <svg className="w-5 h-5 text-cyan-600 dark:text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
              </svg>
            </div>
            <h4 className="text-sm font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wider">Saturación</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {signals.saturation?.message || 'Baja (100% vendedores pequeños)'}
          </p>
        </div>*/}
        
        {/* Cuadro 2: Demanda - Dorado */}
        <div 
          className="relative overflow-hidden p-5 rounded-2xl bg-popover/60 border border-amber-400/40 shadow-[0_0_20px_rgba(251,191,36,0.15)] hover:shadow-[0_0_30px_rgba(251,191,36,0.25)] transition-all duration-300"
          data-testid="strategy-card-demand"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-amber-600 dark:text-amber-400" />
            </div>
            <h4 className="text-sm font-semibold text-amber-700 dark:text-amber-300 uppercase tracking-wider">Demanda</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {signals.demand?.message || `Alta (${fmtNum(avgVisits)} visitas promedio)`}
          </p>
        </div>
        
        {/* Cuadro 3: Crecimiento - Verde */}
        <div 
          className="relative overflow-hidden p-5 rounded-2xl bg-popover/60 border border-emerald-400/40 shadow-[0_0_20px_rgba(52,211,153,0.15)] hover:shadow-[0_0_30px_rgba(52,211,153,0.25)] transition-all duration-300"
          data-testid="strategy-card-growth"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h4 className="text-sm font-semibold text-emerald-700 dark:text-emerald-300 uppercase tracking-wider">Crecimiento</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {signals.growth?.message || `${fmtLocal(facturacionTotal, siteId)} en ventas estimadas - Mercado rentable`}
          </p>
        </div>
        
        {/* Cuadro 4: Catálogos - Morado */}
        <div 
          className="relative overflow-hidden p-5 rounded-2xl bg-popover/60 border border-cyan-400/40 shadow-[0_0_20px_rgba(52,208,227,0.15)] hover:shadow-[0_0_30px_rgba(52,208,227,0.25)] transition-all duration-300"
          data-testid="strategy-card-catalogs"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
              <Tag className="w-5 h-5 text-cyan-600 dark:text-cyan-400" />
            </div>
            <h4 className="text-sm font-semibold text-cyan-700 dark:text-cyan-300 uppercase tracking-wider">Catálogos</h4>
          </div>
          <p className="text-sm text-muted-foreground">
            {signals.catalog?.message || `${m.catalogPct || 0}% productos de catálogo - ${(m.catalogPct || 0) > 50 ? 'Difícil competir' : 'Oportunidad'}`}
          </p>
        </div>
      </div>
      
      {/* Panel Principal: ANÁLISIS DE ESTRATEGIA INTELIGENTE */}
      <StrategicIntelligenceEngine
        results={results}
        siteId={siteId}
        facturacionTotal={facturacionTotal}
        avgVisits={avgVisits}
      />
      
      {/* Facturación Estimada del Nicho - Caja Grande con Gráfico */}
      <div 
        className="relative w-full rounded-2xl bg-gradient-to-br from-card to-muted dark:from-slate-900/80 dark:to-slate-800/50 border border-cyan-400/30 shadow-[0_0_30px_rgba(34,211,238,0.12)] p-6 overflow-hidden"
        data-testid="strategy-revenue-panel"
      >
        {/* Gráfico de fondo - Onda decorativa */}
        <div className="absolute inset-0 opacity-20 pointer-events-none">
          <svg className="w-full h-full" viewBox="0 0 800 200" preserveAspectRatio="none">
            <defs>
              <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#22d3ee" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            <path 
              d="M0,100 C150,150 250,50 400,100 C550,150 650,50 800,100 L800,200 L0,200 Z" 
              fill="url(#waveGradient)"
            />
            <path 
              d="M0,120 C100,80 200,140 350,100 C500,60 600,130 800,90 L800,200 L0,200 Z" 
              fill="url(#waveGradient)"
              opacity="0.5"
            />
          </svg>
        </div>
        
        {/* Contenido */}
        <div className="relative z-10">
          <h3 className="text-lg font-semibold text-cyan-600 dark:text-cyan-400 mb-3 flex items-center gap-2">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Facturación Estimada del Nicho ({siteConfig.currency})
          </h3>

          <div className="flex items-baseline gap-2 mb-3">
            <span className="text-4xl md:text-5xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-teal-600 dark:from-cyan-400 dark:to-teal-300">
              {fmtLocal(facturacionTotal, siteId)}
            </span>
            <span className="text-xl text-cyan-600/70 dark:text-cyan-400/70 font-medium">/mes</span>
          </div>
          
          <p className="text-sm text-cyan-700/60 dark:text-cyan-300/60">
            Suma de ventas brutas estimadas del Top 20 (precio × visitas × 3%)
          </p>
        </div>
        
        {/* Decoración de esquina */}
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-cyan-500/10 to-transparent rounded-bl-full" />
      </div>
    </div>
  );
}

// Motor de Inteligencia Estratégica Central - Cruza Demanda + Mercado
function StrategicIntelligenceEngine({ results, siteId, facturacionTotal, avgVisits }) {
  const items = results.topItems || [];
  const m = results.market || {};

  const UMBRAL_ALTA_FACTURACION = getSiteThreshold(siteId, 'altaFacturacion');
  const TICKET_ALTO = getSiteThreshold(siteId, 'ticketAlto');
  const TICKET_BAJO = getSiteThreshold(siteId, 'ticketBajo');

  // ========== VARIABLES DE DEMANDA ==========
  const pubsAltaDemanda = items.filter(i => (i.visits_30d || 0) > UMBRAL_ALTA_DEMANDA_VISITAS).length;
  const pubsAltaFacturacion = items.filter(i =>
    (i.ventas_brutas_estimadas || 0) > UMBRAL_ALTA_FACTURACION
  ).length;

  const sortedByRevenue = [...items].sort((a, b) => (b.ventas_brutas_estimadas || 0) - (a.ventas_brutas_estimadas || 0));
  const top3Revenue = sortedByRevenue.slice(0, 3).reduce((sum, i) => sum + (i.ventas_brutas_estimadas || 0), 0);
  const pctFacturacionTop3 = facturacionTotal > 0 ? Math.round((top3Revenue / facturacionTotal) * 100) : 0;

  // ========== VARIABLES DE MERCADO ==========
  const totalPublicaciones = m.totalResults || 0;
  const fullPct = m.fullPct || 0;
  const catalogPct = m.catalogPct || 0;

  const prices = items.map(i => i.price || 0).filter(p => p > 0);
  const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;

  // ========== MOTOR LÓGICO DE DEMANDA ==========
  let estadoDemanda = '';
  let analisisDemanda = '';

  if (pubsAltaFacturacion === 0) {
    estadoDemanda = 'sin_profundidad';
    analisisDemanda = `El nicho carece de profundidad comercial. Sin publicaciones que superen ${fmtLocal(UMBRAL_ALTA_FACTURACION, siteId)}. La demanda no está validada.`;
  } else if (pubsAltaFacturacion >= 1 && pubsAltaFacturacion <= 3 && pctFacturacionTop3 > 60) {
    estadoDemanda = 'lideres_aislados';
    analisisDemanda = `Competencia Débil con Líderes Aislados. Solo ${pubsAltaFacturacion} publicación(es) concentran el ${pctFacturacionTop3}% del dinero. La mayoría son novatos que regalan cuota de mercado.`;
  } else if (pubsAltaFacturacion > 5) {
    estadoDemanda = 'sano_distribuido';
    analisisDemanda = `Mercado Sano y Distribuido. ${pubsAltaFacturacion} publicaciones superan ${fmtLocal(UMBRAL_ALTA_FACTURACION, siteId)}. Demanda validada con espacio para varios jugadores.`;
  } else {
    estadoDemanda = 'transicion';
    analisisDemanda = `Mercado en transición. ${pubsAltaFacturacion} pubs con facturación decente pero sin patrón claro de concentración.`;
  }

  // Regla del Espejismo
  let analisisTrafico = '';
  if (avgVisits < 100 && pubsAltaDemanda > 1) {
    analisisTrafico = `El promedio de ${fmtNum(avgVisits)} visitas engaña. Hay ${pubsAltaDemanda} publicaciones captando tráfico cautivo (>${UMBRAL_ALTA_DEMANDA_VISITAS} visitas).`;
  } else if (avgVisits >= 200) {
    analisisTrafico = `Nicho caliente confirmado con ${fmtNum(avgVisits)} visitas promedio. El tráfico orgánico fluye libremente.`;
  } else {
    analisisTrafico = `Tráfico limitado con ${fmtNum(avgVisits)} visitas promedio. La demanda orgánica no justifica inversión agresiva.`;
  }

  // ========== MOTOR LÓGICO DE MERCADO ==========

  let nivelSaturacion = totalPublicaciones > 1000 ? 'alta' : totalPublicaciones >= 500 ? 'moderada' : 'baja';
  let exigenciaFull = fullPct >= 80 ? 'obligatorio' : fullPct >= 41 ? 'oportunidad_media' : 'oportunidad_alta';
  let estadoCatalogo = catalogPct >= 80 ? 'guerra_precios' : catalogPct >= 41 ? 'hibrido' : 'fragmentado';
  let nivelTicket = avgPrice > TICKET_ALTO ? 'alto' : avgPrice >= TICKET_BAJO ? 'medio' : 'bajo';
  let concentracion = pctFacturacionTop3 >= 60 ? 'monopolizado' : 'fragmentado';
  
  // ========== IDENTIFICAR MAYOR BARRERA ==========
  const barreras = [];

  if (estadoCatalogo === 'guerra_precios' && nivelTicket === 'bajo') {
    barreras.push(
      `Trampa de márgenes: catálogo comoditizado (${catalogPct}%) combinado con ticket bajo (${fmtLocal(avgPrice, siteId)}) destruye cualquier rentabilidad. Elevar ticket es supervivencia.`
    );
  }

  if (exigenciaFull === 'obligatorio' && nivelSaturacion === 'alta') {
    barreras.push(
      `Saturación + exigencia logística: ${totalPublicaciones} publicaciones con ${fullPct}% usando FULL. Sin inventario en bodega de MeLi, invisibilidad garantizada.`
    );
  }

  if (concentracion === 'monopolizado') {
    barreras.push(
      `Mercado monopolizado: el ${pctFacturacionTop3}% del dinero fluye hacia 3 vendedores. Exige diferenciación versus líderes.`
    );
  }

  if (estadoCatalogo === 'guerra_precios') {
    barreras.push(
      `Guerra de precios activa: ${catalogPct}% en catálogo implica márgenes comprimidos. Diferenciación visual y empaque premium son obligatorios.`
    );
  }

  if (nivelTicket === 'bajo') {
    barreras.push(
      `Ticket muy bajo (${fmtLocal(avgPrice, siteId)}): el cliente percibe "barato" y los márgenes son milimétricos. Bundles o packs de 2-3 unidades son la única salida.`
    );
  }

  const mayorBarrera = barreras.join('\n\n');
  
  
  // ========== GENERAR VEREDICTO ESTRATÉGICO ==========
  let veredicto = '';
  let colorVeredicto = '';
  
  if (estadoDemanda === 'sin_profundidad' || (avgVisits < 100 && pubsAltaDemanda <= 1)) {
    veredicto = 'NICHO INVIABLE. Sin flujo de dinero validado ni tráfico orgánico suficiente. Evitar inversión.';
    colorVeredicto = 'red';
  } else if (estadoDemanda === 'lideres_aislados' && (exigenciaFull !== 'obligatorio' || nivelSaturacion !== 'alta')) {
    veredicto = 'OPORTUNIDAD DE ASALTO. Líderes aislados dominan un mercado con barreras manejables. Auditar, igualar y robar cuota.';
    colorVeredicto = 'emerald';
  } else if (estadoDemanda === 'sano_distribuido' && nivelTicket !== 'bajo') {
    veredicto = 'MERCADO VALIDADO. Demanda real con márgenes protegibles. Entrada viable con diferenciación clara.';
    colorVeredicto = 'emerald';
  } else if (estadoCatalogo === 'guerra_precios' && nivelTicket === 'bajo') {
    veredicto = 'TRAMPA DE MÁRGENES. Demanda existe pero la estructura de costos destruye rentabilidad. Solo viable con volumen extremo.';
    colorVeredicto = 'red';
  } else {
    veredicto = 'OPORTUNIDAD CONDICIONAL. El nicho tiene potencial pero requiere ejecución precisa en diferenciación y logística.';
    colorVeredicto = 'amber';
  }

  // Clases literales por color de veredicto (emerald/red/amber) — igual que en
  // MarketAnalysisEngine, se escriben completas para que el JIT de Tailwind genere
  // también las variantes dark: en ambos temas.
  const VEREDICTO_CLASSES = {
    emerald: { box: 'bg-emerald-500/15 border-emerald-500/40', label: 'text-emerald-700 dark:text-emerald-400', text: 'text-emerald-800 dark:text-emerald-300' },
    red: { box: 'bg-red-500/15 border-red-500/40', label: 'text-red-700 dark:text-red-400', text: 'text-red-800 dark:text-red-300' },
    amber: { box: 'bg-amber-500/15 border-amber-500/40', label: 'text-amber-700 dark:text-amber-400', text: 'text-amber-800 dark:text-amber-300' },
  };
  const veredictoClasses = VEREDICTO_CLASSES[colorVeredicto] || VEREDICTO_CLASSES.amber;

  // ========== PLAN DE EJECUCIÓN ==========
  
  // Producto & Diferenciación
  const planProducto = estadoCatalogo === 'fragmentado'
    ? 'Control total de tu oferta. Domina con títulos SEO, infografías profesionales y descripciones únicas. Cero guerra de precios.'
    : 'Diferenciación visual obligatoria. Fotos de contexto de uso, no fotos de catálogo sobre fondo blanco.';
  
  // Logística & Posicionamiento
  let planLogistica = '';
  if (exigenciaFull === 'obligatorio') {
    planLogistica = `${fullPct}% de competidores usan FULL. Sin inventario en bodegas de MeLi desde el día 1, serás invisible. No negociable.`;
  } else if (exigenciaFull === 'oportunidad_media') {
    planLogistica = `Solo ${fullPct}% usa FULL. Activarlo te posiciona sobre el ${100-fullPct}% restante. Ventaja competitiva real disponible.`;
  } else {
    planLogistica = `Apenas ${fullPct}% usa FULL. Domina el posicionamiento orgánico simplemente activando logística Full. Oportunidad clara.`;
  }
  
  if (concentracion === 'monopolizado') {
    planLogistica += ` El ${pctFacturacionTop3}% del dinero va a 3 vendedores. NO compitas de frente; ataca su flanco débil con diferenciación.`;
  }
  
  // Estructura de Costos (USD)
  let planCostos = '';
  if (nivelTicket === 'alto') {
    planCostos = `Ticket promedio de ${fmtLocal(avgPrice, siteId)} permite absorber costos de importación, empaque premium y flete sin destruir márgenes. Invierte en calidad percibida.`;
  } else if (nivelTicket === 'medio') {
    planCostos = `Ticket de ${fmtLocal(avgPrice, siteId)} genera fricción. Crea bundle para superar ${fmtLocal(TICKET_ALTO, siteId)} y activar percepción de envío gratis. Alta persuasión visual requerida.`;
  } else {
    planCostos = `Ticket de ${fmtLocal(avgPrice, siteId)} implica márgenes milimétricos. Obligatorio vender packs de 2-3 unidades o incluir accesorios para elevar el ticket y sobrevivir.`;
  }
  
  // Si no hay datos
  if (items.length === 0) {
    return (
      <div 
        className="w-full rounded-2xl bg-gradient-to-r from-amber-500/10 to-yellow-600/5 border border-amber-400/40 shadow-[0_0_25px_rgba(251,191,36,0.15)] p-6"
        data-testid="strategy-header"
      >
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(251,191,36,0.3)]">
            <Target className="w-9 h-9 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-foreground tracking-wide">
              ANÁLISIS DE ESTRATEGIA INTELIGENTE
            </h2>
            <p className="text-amber-700/80 dark:text-amber-300/80 mt-1 text-sm">
              Realiza una búsqueda para obtener el plan de penetración de mercado.
            </p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div 
      className="w-full rounded-2xl bg-gradient-to-br from-amber-500/10 via-card to-orange-600/5 dark:via-slate-900/80 border border-amber-400/40 shadow-[0_0_30px_rgba(251,191,36,0.15)] p-6"
      data-testid="strategy-header"
    >
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <div className="w-14 h-14 rounded-2xl bg-amber-500/20 flex items-center justify-center flex-shrink-0 shadow-[0_0_20px_rgba(251,191,36,0.3)]">
          <Target className="w-8 h-8 text-amber-600 dark:text-amber-400" />
        </div>
        <div>
          <h2 className="text-lg md:text-xl font-bold text-foreground tracking-wide">
            ANÁLISIS DE ESTRATEGIA INTELIGENTE
          </h2>
          <p className="text-amber-700/60 dark:text-amber-300/60 text-xs">Motor de Inteligencia Demanda + Mercado</p>
        </div>
      </div>
      
      <div className="space-y-5">
        {/* Veredicto Estratégico */}
        <div className={`p-4 rounded-xl border ${veredictoClasses.box}`}>
          <div className="flex items-start gap-3">
            <span className="text-2xl">🔥</span>
            <div>
              <span className={`text-xs font-bold uppercase tracking-wider ${veredictoClasses.label}`}>Veredicto Estratégico</span>
              <p className={`text-sm font-semibold mt-1 leading-relaxed ${veredictoClasses.text}`}>{veredicto}</p>
            </div>
          </div>
        </div>
        
        {/* Diagnóstico Cruzado */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🧠</span>
            <span className="text-sm font-bold text-foreground uppercase tracking-wider">Diagnóstico Cruzado</span>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="bg-muted rounded-xl p-4 border-l-3 border-l-cyan-500">
              <p className="text-xs font-semibold text-cyan-600 dark:text-cyan-400 uppercase tracking-wider mb-2">Flujo de Dinero y Tráfico</p>
              <p className="text-sm text-muted-foreground leading-relaxed">{analisisDemanda} {analisisTrafico}</p>
            </div>
            
            <div className="bg-muted rounded-xl p-4 border-l-3 border-l-amber-500">
              <p className="text-xs font-semibold text-amber-600 dark:text-amber-400 uppercase tracking-wider mb-2">Barreras y Fricción</p>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{mayorBarrera}</p>
            </div>
          </div>
        </div>
        
        {/* Plan de Ejecución */}
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <span className="text-lg">🎯</span>
            <span className="text-sm font-bold text-foreground uppercase tracking-wider">Plan de Ejecución Profesional</span>
          </div>
          
          <div className="space-y-2">
            <div className="flex items-start gap-3 bg-muted/40 rounded-lg p-3 border-l-2 border-cyan-500">
              <span className="text-cyan-600 dark:text-cyan-400 font-bold text-sm">•</span>
              <div>
                <span className="text-xs font-semibold text-cyan-700 dark:text-cyan-300 uppercase">Producto & Diferenciación:</span>
                <p className="text-sm text-muted-foreground mt-1">{planProducto}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-muted/40 rounded-lg p-3 border-l-2 border-emerald-500">
              <span className="text-emerald-600 dark:text-emerald-400 font-bold text-sm">•</span>
              <div>
                <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300 uppercase">Logística & Posicionamiento:</span>
                <p className="text-sm text-muted-foreground mt-1">{planLogistica}</p>
              </div>
            </div>
            
            <div className="flex items-start gap-3 bg-muted/40 rounded-lg p-3 border-l-2 border-amber-500">
              <span className="text-amber-600 dark:text-amber-400 font-bold text-sm">•</span>
              <div>
                <span className="text-xs font-semibold text-amber-700 dark:text-amber-300 uppercase">Estructura de Costos:</span>
                <p className="text-sm text-muted-foreground mt-1">{planCostos}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

